import { html, svg, type TemplateResult } from "lit";
import { tableScale } from "./table-scale";

// Arrows between things on the board: targets, attacks and blocks. Elements opt in with
// data-anchor="obj:113 seat:p2 …"; the board measures its arrows after each render and draws them in
// one SVG layer over the table.

export type ArrowTone = "target" | "attack" | "block" | "aim" | "faint";
// `to` is an anchor, or "cursor" for the pointer while aiming or dragging.
export type ArrowSpec = { from: string; to: string; tone: ArrowTone };
export type DrawnArrow = { key: string; d: string; tone: ArrowTone };
export type Point = { x: number; y: number };

type Box = { x: number; y: number; w: number; h: number };

// In the layer's own pixels, which are the screen's divided by the table's scale.
function boxOf(el: Element, origin: DOMRect, k: number): Box {
  const r = el.getBoundingClientRect();
  return { x: (r.left - origin.left + r.width / 2) / k, y: (r.top - origin.top + r.height / 2) / k, w: r.width / k, h: r.height / k };
}

function anchored(root: ShadowRoot, key: string): Element | null {
  return root.querySelector(`[data-anchor~="${CSS.escape(key)}"]`);
}

export function measureArrows(root: ShadowRoot, layer: Element, specs: ArrowSpec[], cursor: Point | null): DrawnArrow[] {
  const origin = layer.getBoundingClientRect();
  const k = tableScale(root);
  const out: DrawnArrow[] = [];
  for (const s of specs) {
    const a = anchored(root, s.from);
    if (!a) continue;
    const from = boxOf(a, origin, k);
    let to: Box;
    if (s.to === "cursor") {
      if (!cursor) continue;
      to = { x: (cursor.x - origin.left) / k, y: (cursor.y - origin.top) / k, w: 0, h: 0 };
    } else {
      const b = anchored(root, s.to);
      if (!b) continue;
      to = boxOf(b, origin, k);
    }
    // The head stops at the target's edge, not its middle, and the line bends a little.
    let sx = from.x;
    let sy = from.y;
    let dx = to.x - sx;
    let dy = to.y - sy;
    let len = Math.hypot(dx, dy) || 1;
    if (s.from === "aim-source") {
      const ux = dx / len;
      const uy = dy / len;
      const edge = Math.min(
        Math.abs(ux) > 0 ? from.w / 2 / Math.abs(ux) : Infinity,
        Math.abs(uy) > 0 ? from.h / 2 / Math.abs(uy) : Infinity,
      ) + 6;
      sx += ux * edge;
      sy += uy * edge;
      dx = to.x - sx;
      dy = to.y - sy;
      len = Math.hypot(dx, dy) || 1;
    }
    const shrink = Math.min(len * 0.35, Math.min(to.w, to.h) * 0.42);
    const ex = to.x - (dx / len) * shrink;
    const ey = to.y - (dy / len) * shrink;
    const bend = s.tone === "aim" ? Math.min(180, len * 0.3) : Math.min(90, len * 0.22);
    const cx = (sx + ex) / 2 - (dy / len) * bend;
    const cy = (sy + ey) / 2 + (dx / len) * bend;
    out.push({
      key: `${s.from}>${s.to}:${s.tone}`,
      d: `M ${sx.toFixed(1)} ${sy.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}`,
      tone: s.tone,
    });
  }
  return out;
}

const TONES: Record<ArrowTone, string> = {
  target: "#c999e6",
  attack: "#e5484d",
  block: "#3ec46d",
  aim: "#48dcff",
  faint: "rgb(221 221 221 / 0.4)",
};

export function arrowLayer(arrows: DrawnArrow[]): TemplateResult {
  return html`<svg class="arrow-layer" aria-hidden="true">
    <defs>
      ${(Object.keys(TONES) as ArrowTone[]).map((t) => svg`
        <marker id="head-${t}" viewBox="0 0 10 10" refX="6" refY="5" markerWidth=${t === "aim" ? 9 : 5} markerHeight=${t === "aim" ? 9 : 5} orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill=${TONES[t]}></path>
        </marker>`)}
    </defs>
    ${arrows.map((a) => svg`
      <path class="arrow-shadow" d=${a.d} stroke-width=${a.tone === "aim" ? 11 : a.tone === "faint" ? 4 : 7}></path>
      <path class="arrow arrow-${a.tone}" d=${a.d} stroke=${TONES[a.tone]} stroke-width=${a.tone === "aim" ? 5 : a.tone === "faint" ? 2 : 4}
        marker-end="url(#head-${a.tone})"></path>`)}
  </svg>`;
}

export function sameArrows(a: DrawnArrow[], b: DrawnArrow[]): boolean {
  return a.length === b.length && a.every((x, i) => x.key === b[i]!.key && x.d === b[i]!.d);
}
